import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ptLocale from "@fullcalendar/core/locales/pt-br";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import IconButton from "@material-ui/core/IconButton";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import TextField from "@material-ui/core/TextField";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import WarningAmberIcon from "@material-ui/icons/Warning";
import LoadingButton from "@material-ui/lab/LoadingButton";
import MobileDateTimePicker from "@material-ui/lab/MobileDateTimePicker";
import DesktopDatePicker from "@material-ui/lab/DesktopDatePicker";
import { getTime } from "date-fns";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import "dayjs/locale/pt-br"; // Importa o idioma PT-BR
import * as Yup from "yup";
import {
  Event,
  EventColor,
  eventColors,
  eventColorsJson,
} from "../types/event";
import {
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputLabel,
} from "@material-ui/core";
import { useCalendar } from "../../core/contexts/CalendarProvider";
import {
  ClientContactsType,
  ClientsType,
  getClientContacts,
} from "../../admin/hooks/useClients";
import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import {
  findAllCandidates,
  findCandidateByCPF,
} from "../../admin/hooks/useCandidate";
import { fetchEvents } from "../hooks/useEvents";
import { fetchParticipants } from "../hooks/useParticipants";
import { getUser } from "../../admin/infra/getUser";
import formatDateNull from "../../core/utils/formatDateNull";
import { Pessoal } from "../../admin/hooks/@types/candidateTypes";
import {
  Alert,
  AlertTitle,
  Chip,
  createTheme,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  Tooltip,
  Typography,
  Link,
} from "@mui/material";
import {
  getSituationAg,
  getTipoAgC,
  getTipoAgR,
  TipoAgType,
  TipoSituationType,
  UsersSysType,
} from "../../admin/hooks/useUtils";
import { useFunctions } from "../../core/contexts/FunctionsProvider";
import compareClients from "../../core/utils/compareClients";
import compareContactsAg from "../../core/utils/compareContactsAg";
import { ptBR as dataGridPtBr } from "@mui/x-data-grid";
import { ptBR as corePtBr } from "@mui/material/locale";
import { ThemeProvider } from "@mui/styles";
import {
  getVacancyAvaliableSYS,
  VacancyListType,
} from "../../admin/hooks/useVacancy";
import {
  SITUACAO_REAGENDADA_CODIGO,
  SITUACAO_REAGENDADA_LABEL,
} from "../constants/agendaSituations";

type EventDialogProps = {
  onAdd: (event: Partial<Event>) => void;
  onClose: () => void;
  onDelete: (eventId: string) => void;
  onUpdate: (event: Event) => void;
  onOpenLinkedEvent?: (eventId: string) => void;
  open: boolean;
  processing: boolean;
  event?: Event;
  hisCanId?: number;
  hisId?: number;
};

type EventFormValues = {
  title: string;
  description?: string;
  start: Date;
  end: Date;
  color?: EventColor;
  type?: string;
  event_type: number | undefined;
  event_situation: number | undefined;
  codchave: Pessoal | undefined;
  contact_id: number | undefined;
};

const EventDialog = ({
  onAdd,
  onClose,
  onDelete,
  onUpdate,
  onOpenLinkedEvent,
  open,
  processing,
  event,
  hisCanId,
  hisId,
}: EventDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { codChave, hasPattern } = useCalendar();

  const initialCodchave = (() => {
    if (event) return event.codchave;
    if (hisCanId && hisCanId >= 1) return hisCanId;
    if (hasPattern("candidate") && codChave) return codChave;
    return undefined;
  })();
  const [openAsyncContacts, setOpenAsyncContacts] = useState(false);
  const [openAsyncCLients, setOpenAsyncClients] = useState(false);

  const [duplicateAsNew, setDuplicateAsNew] = useState(false);
  const [rescheduleAsNew, setRescheduleAsNew] = useState(false);
  const [rescheduleSourceId, setRescheduleSourceId] = useState<string | null>(
    null,
  );

  const plainEditMode =
    Boolean(event && event.id) && !duplicateAsNew && !rescheduleAsNew;
  const lockCoreFields = plainEditMode || rescheduleAsNew;
  const lockScheduleFields = plainEditMode;

  const { clientsSys, usersSys } = useFunctions();

  const convertFormValues = (values: any): Partial<Event> => {
    return {
      ...values,
      start: getTime(values.start),
      end: getTime(values.end),
      type: values.type,
      para_user_id:
        values.convidados_ids?.length > 0
          ? Number(values.convidados_ids[0])
          : undefined,
      reagenda_origem_id:
        values.reagenda_origem_id != null && values.reagenda_origem_id !== ""
          ? Number(values.reagenda_origem_id)
          : undefined,
    };
  };

  const handleSubmit = (values: Event) => {
    const newEvent = convertFormValues(values);
    if (event && event.id && !duplicateAsNew && !rescheduleAsNew) {
      onUpdate({ ...newEvent, id: event.id } as Event);
    } else {
      onAdd(newEvent);
    }
  };

  const addDefaultDuration = (date: Date) => {
    return new Date(date.getTime() + 15 * 60 * 1000);
  };

  const [agTypeSting, setAgTypeSting] = useState("");
  const [agNomeSting, setAgNomeSting] = useState("");
  const pastDateTimeMessage =
    "Não é permitido agendar com data e horário anteriores ao atual";

  const formik = useFormik({
    initialValues: {
      id: event?.id ? event.id : "",
      title: event ? event.title : "",
      hisId: hisId || null,
      description: event ? event.description : undefined,
      start: event ? new Date(event.start) : new Date(),
      end: event ? new Date(event.end) : addDefaultDuration(new Date()),
      color: event ? event.color : "primary",
      type: event ? String(event.type) : "1",
      event_type: event?.event_type ? String(event.event_type) : undefined,
      event_situation: event?.reagenda_origem_id
        ? String(SITUACAO_REAGENDADA_CODIGO)
        : event?.event_situation
          ? String(event.event_situation)
          : undefined,
      codchave: initialCodchave,
      codchave_OBJ: event ? undefined : undefined,
      contact_id: event ? event.contact_id : undefined,
      vaga_id: event ? event.vaga_id : undefined,
      cliente_ag_id: event ? event.cliente_ag_id : undefined,
      para_user_id: event ? event.para_user_id : undefined,
      convidados_ids: (() => {
        const base = event?.para_user_id ? [Number(event.para_user_id)] : [];
        const extra = Array.isArray(event?.participants)
          ? (event.participants as number[]).map(Number).filter((id) => !base.includes(id))
          : [];
        return [...base, ...extra];
      })(),
      reagenda_origem_id:
        event?.reagenda_origem_id != null && event?.reagenda_origem_id !== ""
          ? String(event.reagenda_origem_id)
          : "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required(t("common.validations.required")),
      event_type: Yup.string().required(t("common.validations.required")),
      event_situation: Yup.string(),
      type: Yup.string().required(t("common.validations.required")),
      description: Yup.string()
        .required(t("common.validations.required"))
        .max(100, t("common.validations.max", { size: 100 })),
      start: Yup.date()
        .required(t("common.validations.required"))
        .test(
          "not-in-past",
          pastDateTimeMessage,
          function (value) {
            const agType = this.parent?.type;
            if (plainEditMode || agType === "3") return true;
            return (
              !value || new Date(value).getTime() >= Date.now()
            );
          },
        ),
      end: Yup.date().required(t("common.validations.required")),
      color: Yup.string(),
      codchave: Yup.string().when("type", {
        is: (type: string) => type === "1" || type === "2" || type === "3",
        then: Yup.string().required(t("common.validations.required")),
        otherwise: Yup.string(),
      }),
    }),
    //onSubmit: (values) => console.log(values),
    onSubmit: handleSubmit,
  });

  const myTheme = createTheme(corePtBr, dataGridPtBr);
  const [loading, isLoading] = useState(false);
  const [loadingCli, isLoadingCli] = useState(false);

  const [loadingType, isLoadingType] = useState(false);
  const [loadingSituation, isLoadingSituation] = useState(false);
  const [contact, setContact] = useState<ClientContactsType | null>(null);

  const [candidate, setCandidate] = useState<
    Pessoal | string | null | undefined
  >(null);
  const [optionsContacts, setOptions] = useState<readonly ClientContactsType[]>(
    [],
  );
  const [optionsCandidates, setOptionsCandidates] = useState<Pessoal[]>([]);
  const [cpfSearch, setCpfSearch] = useState("");
  const [cpfStatus, setCpfStatus] = useState<
    "idle" | "searching" | "found" | "not_found"
  >("idle");
  const [guestEventsMap, setGuestEventsMap] = useState<
    Record<number, Event[]>
  >({});

  const formatCPF = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  };

  const handleCpfChange = async (raw: string) => {
    const formatted = formatCPF(raw);
    setCpfSearch(formatted);
    const digits = formatted.replace(/\D/g, "");
    if (digits.length < 11) {
      setCpfStatus("idle");
      return;
    }
    setCpfStatus("searching");
    const result = await findCandidateByCPF(digits);
    if (!result) {
      setCpfStatus("not_found");
      return;
    }
    const filterValues = {
      search: result.pessoal_codigo.toString(),
      sex: "0",
      cagos: [],
      instrucoes: "0",
      salario: "999999.99",
      cvatualizado_min: formatDateNull("", "yyyy-MM-dd"),
      cvatualizado_max: formatDateNull("", "yyyy-MM-dd"),
      entrevistados: false,
      idade_min: "",
      idade_max: "",
    };
    const resultData = await findAllCandidates(filterValues);
    const candidates: Pessoal[] = resultData?.body || [];
    setOptionsCandidates(candidates);
    const matched = candidates.find(
      (c) => Number(c.id) === Number(result.pessoal_codigo),
    );
    if (matched) {
      formik.setFieldValue("codchave", matched.id);
      formik.setFieldValue("codchave_OBJ", matched);
      setAgNomeSting(matched.pessoal_nome);
      setCpfStatus("found");
    } else {
      setCpfStatus("not_found");
    }
  };
  const [optionsVacancies, setOptionsVacancies] = useState<VacancyListType[]>(
    [],
  );

  const [optionsType, setOptionsType] = useState<TipoAgType[]>([]);

  const [optionsSituation, setOptionsSituation] = useState<TipoSituationType[]>(
    [],
  );

  const handleDuplicate = () => {
    setDuplicateAsNew(true);
    setRescheduleAsNew(false);
    setRescheduleSourceId(null);
    formik.setValues({
      ...formik.values,
      id: "",
      reagenda_origem_id: "",
    });
    formik.setTouched({});
  };

  const handleReschedule = () => {
    if (!event?.id) return;
    setRescheduleSourceId(String(event.id));
    setRescheduleAsNew(true);
    setDuplicateAsNew(false);
    formik.setValues({
      ...formik.values,
      id: "",
      event_situation: String(SITUACAO_REAGENDADA_CODIGO),
      reagenda_origem_id: String(event.id),
    });
    formik.setTouched({});
  };

  async function buscarContatoPorCodigo(
    jsonData: ClientContactsType[],
  ): Promise<ClientContactsType | null> {
    for (const contato of jsonData) {
      if (contato.id === event?.contact_id) {
        return contato;
      }
    }
    return null;
  }
  let abortController = new AbortController();

  async function getCandidate(
    e?: React.SyntheticEvent<Element, globalThis.Event> | undefined | null,
    search?: Pessoal | string | undefined | null,
    autoSelect?: boolean,
  ) {
    e?.preventDefault();

    const filterValues = {
      search:
        (e?.target instanceof HTMLInputElement ? e.target.value : "") ||
        search ||
        event?.codchave,
      sex: "0",
      cagos: [],
      instrucoes: "0",
      salario: "999999.99",
      cvatualizado_min: formatDateNull("", "yyyy-MM-dd"),
      cvatualizado_max: formatDateNull("", "yyyy-MM-dd"),
      entrevistados: false,
      idade_min: "",
      idade_max: "",
    };
    isLoading(true);

    const resultData = await findAllCandidates(filterValues);
    const candidates: Pessoal[] = resultData?.body || [];
    setOptionsCandidates(candidates);

    if (autoSelect && candidates.length) {
      const raw =
        (e?.target instanceof HTMLInputElement ? e.target.value : undefined) ??
        search ??
        event?.codchave ??
        "";
      const needle = String(raw).trim();
      const byId = candidates.find(
        (c) =>
          String(c.id) === needle ||
          (c.pessoal_codigo != null &&
            String(c.pessoal_codigo) === needle),
      );
      if (byId) {
        formik.setFieldValue("codchave", byId.id);
        formik.setFieldValue("codchave_OBJ", byId);
        setAgNomeSting(byId.pessoal_nome || "");
      }
    }

    isLoading(false);
  }

  async function getVacancies() {
    isLoading(true);
    try {
      const result = await getVacancyAvaliableSYS();
      setOptionsVacancies(result as VacancyListType[]);
    } catch (err) {
      console.log(err);
      setOptionsVacancies([]);
    }
    isLoading(false);
  }

  async function populateTipoAg(type: string) {
    isLoadingType(true);

    let result;

    if (type == "1") {
      result = await getTipoAgR();
    }
    if (type == "3") {
      result = await getTipoAgC();
    }
    setOptionsType(result as TipoAgType[]);
    isLoadingType(false);
  }

  async function populateSituationAg(type: string) {
    isLoadingSituation(true);

    let result;

    if (type == "1") {
      result = await getSituationAg("sitagR");
    }
    if (type == "3") {
      result = await getSituationAg("sitagC");
    }
    setOptionsSituation(result as TipoSituationType[]);
    isLoadingSituation(false);
  }

  useEffect(() => {
    getContacts(Number(codChave)).then(async (res) => {
      const searshContacts = await buscarContatoPorCodigo(res);
      console.log(searshContacts);
      setContact(searshContacts);
    });
    getVacancies();
    if (!event?.type) {
      if (hasPattern("clients")) {
        formik.setFieldValue("type", "3");
      } else if (hasPattern("vacancy")) {
        if (hisId) {
          formik.setFieldValue("type", "1");
        } else {
          formik.setFieldValue("type", "2");
        }
      } else if (hasPattern("candidate")) {
        formik.setFieldValue("type", "1");
      } else {
        formik.setFieldValue("type", "");
      }
    }
  }, []);

  const getContacts = async (id: number): Promise<any | boolean> => {
    const data = await getClientContacts(id);
    if (data as any) {
      setOptions(data as ClientContactsType[]);
      return data;
    }
    return false;
  };

  useEffect(() => {
    if (plainEditMode) {
      if (formik.values.type == "1") {
        populateTipoAg("1");
        populateSituationAg("1");
      }
      if (formik.values.type == "2") {
        populateTipoAg("1");
        populateSituationAg("1");
      }
      if (formik.values.type == "3") {
        populateTipoAg("3");
        populateSituationAg("3");
      }
    } else {
      if (formik.values.type == "1") {
        populateTipoAg("1");
        populateSituationAg("1");
        formik.setFieldValue("event_type", "");
        formik.setFieldValue("event_situation", "");
      }
      if (formik.values.type == "2") {
        populateTipoAg("1");
        populateSituationAg("1");
        formik.setFieldValue("event_type", "");
        formik.setFieldValue("event_situation", "");
      }
      if (formik.values.type == "3") {
        populateTipoAg("3");
        populateSituationAg("3");
        formik.setFieldValue("event_type", "");
        formik.setFieldValue("event_situation", "");
      }
    }
  }, [formik.values.type]);

  useEffect(() => {
    if (plainEditMode && formik.values.codchave != undefined) {
      //@ts-ignore
      getCandidate(undefined, formik.values.codchave, true);
    }
    if (hisCanId && hisCanId >= 1) {
      getCandidate(undefined, hisCanId.toString(), true);
    }
  }, [formik.values.codchave]);

  useEffect(() => {
    if (plainEditMode && event?.id) {
      fetchParticipants(event.id).then((ids) => {
        if (ids.length > 0) {
          const base = event.para_user_id ? [Number(event.para_user_id)] : [];
          const merged = [...base, ...ids.filter((id) => !base.includes(id))];
          formik.setFieldValue("convidados_ids", merged);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (formik.values.type == "3") {
      getContacts(Number(formik.values.codchave));
      formik.setFieldValue("contact_id", undefined);
      setContact(null);
    }
  }, [formik.values.codchave]);

  function setTile(codchave: string, agtype: string) {
    formik.setFieldValue("title", codchave + " " + agtype);
  }

  useEffect(() => {
    if (duplicateAsNew || rescheduleAsNew) return;
    setTile(agNomeSting, agTypeSting);
  }, [agTypeSting, agNomeSting, duplicateAsNew, rescheduleAsNew]);

  useEffect(() => {
    setDuplicateAsNew(false);
    setRescheduleAsNew(false);
    setRescheduleSourceId(null);
  }, [event?.id, open]);

  useEffect(() => {
    const ids = formik.values.convidados_ids as number[];
    ids.forEach((id) => {
      if (guestEventsMap[id] === undefined) {
        fetchEvents(id).then((events) => {
          setGuestEventsMap((prev) => ({ ...prev, [id]: events }));
        });
      }
    });
  }, [formik.values.convidados_ids]);

  const showGuestPanel =
    (formik.values.convidados_ids as number[]).length > 0;

  const conflictingUsers = showGuestPanel
    ? (formik.values.convidados_ids as number[])
        .filter((userId) => {
          if (guestEventsMap[userId] === undefined) return false;
          const evtStart = new Date(formik.values.start);
          const evtEnd = new Date(formik.values.end);
          return (guestEventsMap[userId] || []).some((e) => {
            if (event?.id && String(e.id) === String(event.id)) return false;
            const eStart = new Date(Number(e.start));
            const eEnd = new Date(Number(e.end));
            return (
              eStart.toDateString() === evtStart.toDateString() &&
              evtStart < eEnd &&
              evtEnd > eStart
            );
          });
        })
        .map(
          (userId) =>
            usersSys.find((u) => Number(u.id) === userId)?.nomecomp ||
            `Usuário ${userId}`,
        )
    : [];

  const hasGuestConflict = conflictingUsers.length > 0;

  const [conflictDismissed, setConflictDismissed] = useState(false);
  const prevConflictRef = useRef(false);
  const guestPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasGuestConflict && !prevConflictRef.current) {
      setConflictDismissed(false);
    }
    if (!hasGuestConflict) {
      setConflictDismissed(false);
    }
    prevConflictRef.current = hasGuestConflict;
  }, [hasGuestConflict]);

  const timeSlots = (() => {
    const slots: string[] = [];
    for (let h = 8; h <= 18; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (h === 18 && m > 0) break;
        slots.push(
          `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
        );
      }
    }
    return slots;
  })();

  const getTimeString = (date: Date) =>
    `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

  const applyTimeToDate = (date: Date, timeStr: string): Date => {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return date;
    const updated = new Date(date);
    updated.setHours(Number(match[1]));
    updated.setMinutes(Number(match[2]));
    updated.setSeconds(0);
    return updated;
  };

  const handleStartTimeChange = (timeStr: string) => {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return;
    const duration =
      new Date(formik.values.end).getTime() -
      new Date(formik.values.start).getTime();
    const newStart = applyTimeToDate(new Date(formik.values.start), timeStr);
    const newEnd = new Date(newStart.getTime() + duration);
    formik.setFieldValue("start", newStart);
    formik.setFieldValue("end", newEnd);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="event-dialog-title"
      fullWidth
      maxWidth={showGuestPanel ? "lg" : "sm"}
    >
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle
          id="event-dialog-title"
          sx={{
            position: "relative",
            pr:
              event?.id && !duplicateAsNew && !rescheduleAsNew
                ? 7
                : undefined,
            pt: 2,
            pb: 1,
          }}
        >
          {rescheduleAsNew
            ? "Reagendar"
            : plainEditMode
              ? t("calendar.modal.edit.title")
              : t("calendar.modal.add.title")}
          {event && event.id && !duplicateAsNew && !rescheduleAsNew && (
            <IconButton
              aria-label="delete event"
              size="small"
              onClick={() => onDelete(event.id)}
              disabled={processing}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "grey.600",
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
          <Grid container spacing={1}>
            {(Boolean(event?.reagenda_origem_id) ||
              (rescheduleAsNew && rescheduleSourceId)) && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  variant="filled"
                  label="Reagendamento"
                  value="Esta agenda está vinculada a um compromisso anterior reagendado. A situação foi definida como Reagendada e não pode ser alterada."
                  InputProps={{ readOnly: true }}
                  multiline
                  minRows={2}
                />
                <Box sx={{ mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    underline="always"
                    sx={{ cursor: "pointer", fontSize: "0.875rem" }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenLinkedEvent?.(
                        rescheduleAsNew && rescheduleSourceId
                          ? rescheduleSourceId
                          : String(event?.reagenda_origem_id),
                      );
                    }}
                  >
                    Abrir agenda vinculada
                    {event?.reagenda_origem_title
                      ? ` (${event.reagenda_origem_title})`
                      : rescheduleAsNew && event?.title
                        ? ` (${event.title})`
                        : ""}
                  </Link>
                </Box>
              </Grid>
            )}
            {/*       <Grid item xs={12} md={12}>
              <TextField
                margin="normal"
                size="small"
                required
                fullWidth
                id="title"
                label={t("calendar.form.title.label")}
                name="title"
                autoFocus
                disabled={lockCoreFields || processing}
                value={formik.values.title}
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid> */}

            <Grid item xs={6} md={6}>
              <TextField
                label="Proprietária da agenda"
                size="small"
                variant="filled"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={
                  plainEditMode && !rescheduleAsNew
                    ? event?.de_user_nome || ""
                    : getUser().userName || ""
                }
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <Autocomplete
                multiple
                id="convidados_ids"
                options={usersSys}
                value={usersSys.filter((u) =>
                  (formik.values.convidados_ids as number[]).includes(Number(u.id)),
                )}
                isOptionEqualToValue={(option, value) =>
                  Number(option.id) === Number(value.id)
                }
                getOptionLabel={(option: UsersSysType) => option.nomecomp}
                onChange={(_e: any, newValues: UsersSysType[]) => {
                  formik.setFieldValue(
                    "convidados_ids",
                    newValues.map((u) => Number(u.id)),
                  );
                }}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.nomecomp}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Convidados"
                    size="small"
                    variant="filled"
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      (formik.values.convidados_ids as number[]).length === 0
                        ? "Adicionar convidados..."
                        : ""
                    }
                  />
                )}
              />
            </Grid>

            {isMobile ? (
              <Grid item xs={12} md={12}>
                <MobileDateTimePicker
                  label={t("calendar.form.start.label")}
                  className="tabsEdited"
                  inputFormat="dd/MM/yyyy H:mm"
                  disablePast={formik.values.type !== "3"}
                  value={formik.values.start}
                    onChange={(date: Date | null) => {
                    if (!date) return;
                    const duration =
                      new Date(formik.values.end).getTime() -
                      new Date(formik.values.start).getTime();
                    formik.setFieldValue("start", date);
                    formik.setFieldValue(
                      "end",
                      new Date(date.getTime() + duration),
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="start"
                      label={t("calendar.form.start.label")}
                      size="small"
                      variant="filled"
                      error={
                        formik.touched.start && Boolean(formik.errors.start)
                      }
                      disabled={lockScheduleFields || processing}
                      fullWidth
                      margin="normal"
                      name="start"
                    />
                  )}
                />
                {formik.touched.start && (
                  <FormHelperText error>{formik.errors.start}</FormHelperText>
                )}
              </Grid>
            ) : (
              <>
                <Grid item xs={5} md={5}>
                  <DesktopDatePicker
                    label={t("calendar.form.start.label")}
                    inputFormat="dd/MM/yyyy"
                    disablePast={formik.values.type !== "3"}
                    value={formik.values.start}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      const current = new Date(formik.values.start);
                      date.setHours(current.getHours());
                      date.setMinutes(current.getMinutes());
                      date.setSeconds(0);
                      const duration =
                        new Date(formik.values.end).getTime() -
                        new Date(formik.values.start).getTime();
                      formik.setFieldValue("start", date);
                      formik.setFieldValue(
                        "end",
                        new Date(date.getTime() + duration),
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("calendar.form.start.label")}
                        size="small"
                        variant="filled"
                        error={
                          formik.touched.start && Boolean(formik.errors.start)
                        }
                        disabled={lockScheduleFields || processing}
                        fullWidth
                        margin="normal"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={3} md={3}>
                  <Autocomplete
                    freeSolo
                    options={timeSlots}
                    disabled={lockScheduleFields || processing}
                    value={getTimeString(new Date(formik.values.start))}
                    onChange={(_e, value) => {
                      if (value) { handleStartTimeChange(value); }
                    }}
                    onBlur={(e) => {
                      const val = (e.target as HTMLInputElement).value;
                      handleStartTimeChange(val);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Início"
                        size="small"
                        variant="filled"
                        fullWidth
                        margin="normal"
                        disabled={lockScheduleFields || processing}
                        InputLabelProps={{ shrink: true }}
                        placeholder="08:00"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={3} md={3}>
                  <Autocomplete
                    freeSolo
                    disabled={lockScheduleFields || processing}
                    options={timeSlots}
                    value={getTimeString(new Date(formik.values.end))}
                    onChange={(_e, value) => {
                      if (value) {
                        formik.setFieldValue(
                          "end",
                          applyTimeToDate(new Date(formik.values.end), value),
                        );
                      }
                    }}
                    onBlur={(e) => {
                      const val = (e.target as HTMLInputElement).value;
                      formik.setFieldValue(
                        "end",
                        applyTimeToDate(new Date(formik.values.end), val),
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Término"
                        size="small"
                        variant="filled"
                        fullWidth
                        margin="normal"
                        disabled={lockScheduleFields || processing}
                        InputLabelProps={{ shrink: true }}
                        placeholder="09:00"
                      />
                    )}
                  />
                </Grid>
                {formik.touched.start && (
                  <Grid item xs={12}>
                    <FormHelperText error>{formik.errors.start}</FormHelperText>
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12} md={12}>
              <FormControl component="fieldset" margin="normal">
                <FormLabel component="animate">Tipo da Agenda</FormLabel>
                <RadioGroup
                  row
                  aria-label="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  defaultValue="first"
                >
                  {(plainEditMode || rescheduleAsNew) ? (
                    <>
                      {eventColorsJson.map((item) => (
                        <>
                          {item.value === formik.values.type && (
                            <FormControlLabel
                              key={item.value}
                              value={item.value}
                              control={
                                <Radio
                                  size="small"
                                  disabled={lockCoreFields || processing}
                                  sx={{
                                    color: `${item.color}.main`,
                                    "&.Mui-checked": {
                                      color: `${item.color}.main`,
                                    },
                                  }}
                                />
                              }
                              label={item.label}
                            />
                          )}
                        </>
                      ))}
                    </>
                  ) : (
                    <>
                      {eventColorsJson.map((item) => (
                        <FormControlLabel
                          key={item.value}
                          value={item.value}
                  control={
                                <Radio
                                  size="small"
                                  disabled={lockCoreFields || processing}
                              sx={{
                                color: `${item.color}.main`,
                                "&.Mui-checked": {
                                  color: `${item.color}.main`,
                                },
                              }}
                            />
                          }
                          label={item.label}
                        />
                      ))}
                    </>
                  )}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <FormControl fullWidth style={{ marginTop: 16 }} variant="filled">
                <InputLabel
                  error={
                    formik.touched.event_type &&
                    Boolean(formik.errors.event_type)
                  }
                  shrink
                  id="event_type_label"
                >
                  Tipo da Agenda
                </InputLabel>
                <Select
                  labelId="event_type_label"
                  id="event_type"
                  variant="filled"
                  name="event_type"
                  size="small"
                  autoComplete="off"
                  disabled={lockCoreFields || loadingType}
                  label="Tipo da Agenda"
                  value={formik.values.event_type}
                  error={
                    formik.touched.event_type &&
                    Boolean(formik.errors.event_type)
                  }
                  onChange={formik.handleChange}
                  endAdornment={
                    loadingType && (
                      <InputAdornment position="end">
                        <CircularProgress size={13} />
                      </InputAdornment>
                    )
                  }
                >
                  {optionsType.map((item, i) => (
                    <MenuItem
                      key={i}
                      className="camelize"
                      value={`${item.codigo}`}
                      onClick={() => setAgTypeSting(item.descricao)}
                    >
                      {item.descricao}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.event_type && (
                  <FormHelperText error>
                    {formik.errors.event_type}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              {rescheduleAsNew || Boolean(event?.reagenda_origem_id) ? (
                <TextField
                  fullWidth
                  label="Situação da Agenda"
                  value={SITUACAO_REAGENDADA_LABEL}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  size="small"
                  style={{ marginTop: 16 }}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <FormControl fullWidth style={{ marginTop: 16 }} variant="filled">
                  <InputLabel
                    shrink
                    id="event_situation_label"
                  >
                    Situação da Agenda
                  </InputLabel>
                  <Select
                    labelId="event_situation_label"
                    id="event_situation"
                    name="event_situation"
                    size="small"
                    variant="filled"
                    autoComplete="off"
                    disabled={loadingSituation}
                    label="Situação da Agenda"
                    value={formik.values.event_situation}
                    onChange={formik.handleChange}
                    endAdornment={
                      loadingSituation && (
                        <InputAdornment position="end">
                          <CircularProgress size={13} />
                        </InputAdornment>
                      )
                    }
                  >
                    {optionsSituation.map((item, i) => (
                      <MenuItem
                        key={i}
                        className="camelize"
                        value={`${item.codigo}`}
                      >
                        {item.descricao}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            {formik.values.type == "1" && (
              <>
                {(plainEditMode || rescheduleAsNew) ? (
                  <>
                    {!formik.values.codchave_OBJ && (
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          mt: 1,
                          mb: 1,
                        }}
                      >
                        <Typography>Procurando Candidato</Typography>
                        <CircularProgress sx={{ ml: 2 }} />
                      </Grid>
                    )}
                    {formik.values.codchave_OBJ && (
                      <Grid item xs={12} md={12} lg={12}>
                        <Autocomplete
                          id="codchave"
                          disabled={lockCoreFields}
                          open={openAsyncCLients}
                          onOpen={() => {
                            setOpenAsyncClients(true);
                          }}
                          onClose={() => {
                            setOpenAsyncClients(false);
                          }}
                          value={formik.values.codchave_OBJ}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          getOptionLabel={(option) =>
                            `${option.id} - (${option.pessoal_nome}) - ${option.pessoal_cidade} em ${option.pessoal_bairro}`
                          }
                          options={optionsCandidates}
                          loading={loading}
                          onChange={(event: any, newValue: any | null) => {
                            if (newValue) {
                              formik.setFieldValue("codchave", newValue?.id);
                              formik.setFieldValue("codchave_OBJ", newValue);
                              setAgNomeSting(newValue?.pessoal_nome);
                            }
                          }}
                          onInputChange={(e) => {
                            getCandidate(e);
                          }}
                          renderOption={(props, option) => (
                            <Box
                              key={option.id}
                              component="li"
                              sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                              {...props}
                            >
                              <Grid container>
                                <Grid item xs={12}>
                                  <b>{option.id}</b> {option.pessoal_nome}
                                </Grid>
                                <Grid
                                  item
                                  xs={12}
                                  style={{
                                    fontSize: "12px",
                                  }}
                                >
                                  Cidade: <b>{option.pessoal_cidade}</b>
                                </Grid>
                                {option.pessoal_bairro && (
                                  <Grid
                                    item
                                    xs={12}
                                    style={{
                                      fontSize: "12px",
                                    }}
                                  >
                                    Bairro: <b>{option.pessoal_bairro}</b>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Candidato"
                              placeholder="Digite para buscar..."
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              autoComplete="off"
                              variant="filled"
                              error={
                                formik.touched.codchave &&
                                Boolean(formik.errors.codchave)
                              }
                              fullWidth
                            />
                          )}
                        />
                        {formik.touched.codchave && (
                          <FormHelperText error>
                            {formik.errors.codchave}
                          </FormHelperText>
                        )}
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    {hisCanId && hisCanId >= 1 ? (
                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        sx={{
                          border: "1px solid #f3f3f3",
                          background: "#f3f3f3",
                          borderRadius: 1,
                          mt: 1,
                          ml: 1,
                        }}
                      >
                        <Typography variant="h5">Candidato</Typography>
                        <Typography>
                          {/* //@ts-ignore */}
                          {formik.values.codchave_OBJ ? (
                            //@ts-ignore
                            formik.values.codchave_OBJ.pessoal_nome
                          ) : (
                            <Grid
                              container
                              justifyContent="center"
                              alignItems="center"
                              sx={{
                                mt: 1,
                                mb: 1,
                              }}
                            >
                              <Typography>Procurando Candidato</Typography>
                              <CircularProgress sx={{ ml: 2 }} />
                            </Grid>
                          )}
                        </Typography>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12} md={12} lg={12}>
                          <TextField
                            label="CPF"
                            size="small"
                            variant="filled"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            placeholder="000.000.000-00"
                            value={cpfSearch}
                            onChange={(e) => handleCpfChange(e.target.value)}
                            disabled={lockCoreFields || processing}
                            InputProps={{
                              endAdornment:
                                cpfStatus === "searching" ? (
                                  <InputAdornment position="end">
                                    <CircularProgress size={16} />
                                  </InputAdornment>
                                ) : undefined,
                            }}
                            error={cpfStatus === "not_found"}
                            helperText={
                              cpfStatus === "not_found"
                                ? "CPF não encontrado"
                                : cpfStatus === "found"
                                  ? "Candidato encontrado"
                                  : ""
                            }
                            FormHelperTextProps={{
                              sx: {
                                color:
                                  cpfStatus === "found"
                                    ? "success.main"
                                    : undefined,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} lg={12}>
                          <Autocomplete
                            id="codchave"
                            open={openAsyncCLients}
                            onOpen={() => {
                              setOpenAsyncClients(true);
                            }}
                            onClose={() => {
                              setOpenAsyncClients(false);
                            }}
                            value={formik.values.codchave_OBJ || null}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            getOptionLabel={(option) =>
                              `${option.id} - (${option.pessoal_nome}) - ${option.pessoal_cidade} em ${option.pessoal_bairro}`
                            }
                            options={optionsCandidates}
                            loading={loading}
                            onChange={(event: any, newValue: any | null) => {
                              if (newValue) {
                                formik.setFieldValue("codchave", newValue?.id);
                                formik.setFieldValue("codchave_OBJ", newValue);
                                setAgNomeSting(newValue?.pessoal_nome);
                              }
                            }}
                            onInputChange={(e) => {
                              getCandidate(e);
                            }}
                            renderOption={(props, option) => (
                              <Box
                                key={option.id}
                                component="li"
                                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                                {...props}
                              >
                                <Grid container>
                                  <Grid item xs={12}>
                                    <b>{option.id}</b> {option.pessoal_nome}
                                  </Grid>
                                  <Grid
                                    item
                                    xs={12}
                                    style={{
                                      fontSize: "12px",
                                    }}
                                  >
                                    Cidade: <b>{option.pessoal_cidade}</b>
                                  </Grid>
                                  {option.pessoal_bairro && (
                                    <Grid
                                      item
                                      xs={12}
                                      style={{
                                        fontSize: "12px",
                                      }}
                                    >
                                      Bairro: <b>{option.pessoal_bairro}</b>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Candidato"
                                placeholder="Digite para buscar..."
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                autoComplete="off"
                                variant="filled"
                                error={
                                  formik.touched.codchave &&
                                  Boolean(formik.errors.codchave)
                                }
                                fullWidth
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {loading ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                }}
                              />
                            )}
                          />
                          {formik.touched.codchave && (
                            <FormHelperText error>
                              {formik.errors.codchave}
                            </FormHelperText>
                          )}
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid item xs={6} md={6} lg={6}>
                  <Autocomplete
                    id="vaga_id"
                    disabled={lockCoreFields}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) =>
                      `${option.id} - ${option.cargo_nome}${option.cliente_nome ? ` - ${option.cliente_nome}` : ""}`
                    }
                    options={optionsVacancies}
                    value={
                      optionsVacancies.find(
                        (v) => v.id === Number(formik.values.vaga_id),
                      ) || null
                    }
                    onChange={(_e: any, newValue: VacancyListType | null) => {
                      formik.setFieldValue(
                        "vaga_id",
                        newValue ? newValue.id : undefined,
                      );
                      if (newValue?.cliente_nome && clientsSys) {
                        const matched = clientsSys.find(
                          (c) =>
                            c.nome.toLowerCase() ===
                            newValue.cliente_nome.toLowerCase(),
                        );
                        if (matched) {
                          formik.setFieldValue("cliente_ag_id", matched.id);
                        }
                      }
                    }}
                    renderOption={(props, option) => (
                      <Box
                        key={option.id}
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <Grid container>
                          <Grid item xs={12}>
                            <b>#{option.id}</b> {option.cargo_nome}
                          </Grid>
                          {option.cliente_nome && (
                            <Grid item xs={12} style={{ fontSize: "12px" }}>
                              Cliente: <b>{option.cliente_nome}</b>
                            </Grid>
                          )}
                          <Grid item xs={12} style={{ fontSize: "12px" }}>
                            Cidade: <b>{option.cidade}</b>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vaga (opcional)"
                        placeholder="Selecione uma vaga..."
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        autoComplete="off"
                        variant="filled"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  {clientsSys && (
                    <Autocomplete
                      id="cliente_ag_id"
                      disabled={lockCoreFields}
                      value={compareClients(
                        formik.values.cliente_ag_id,
                        clientsSys,
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) =>
                        `${option.id} - (${option.nome})`
                      }
                      options={clientsSys}
                      onChange={(_e: any, newValue: ClientsType | null) => {
                        formik.setFieldValue(
                          "cliente_ag_id",
                          newValue ? newValue.id : undefined,
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cliente"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          autoComplete="off"
                          variant="filled"
                          fullWidth
                        />
                      )}
                    />
                  )}
                </Grid>
              </>
            )}
            {formik.values.type == "2" && (
              <Grid item xs={12} md={12} lg={12}>
                <Autocomplete
                  id="codchave"
                      disabled={lockCoreFields}
                  open={openAsyncCLients}
                  onOpen={() => {
                    setOpenAsyncClients(true);
                  }}
                  onClose={() => {
                    setOpenAsyncClients(false);
                  }}
                  value={
                    optionsVacancies.find(
                      (v) => v.id === Number(formik.values.codchave),
                    ) || null
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) =>
                    `${option.id} - ${option.cargo_nome} - ${option.cidade} - ${
                      option.cliente_nome || "Cliente não informado"
                    }`
                  }
                  options={optionsVacancies}
                  loading={loading}
                  onChange={(event: any, newValue: VacancyListType | null) => {
                    if (newValue) {
                      formik.setFieldValue("codchave", newValue?.id);
                      setAgNomeSting(
                        `Vaga ${newValue?.id} - ${newValue?.cargo_nome}`,
                      );
                    }
                  }}
                  renderOption={(props, option) => (
                    <Box
                      key={option.id}
                      component="li"
                      sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                      {...props}
                    >
                      <Grid container>
                        <Grid item xs={12}>
                          <b>#{option.id}</b> {option.cargo_nome}
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          Cidade: <b>{option.cidade}</b>
                        </Grid>
                        {option.cliente_nome && (
                          <Grid
                            item
                            xs={12}
                            style={{
                              fontSize: "12px",
                            }}
                          >
                            Cliente: <b>{option.cliente_nome}</b>
                          </Grid>
                        )}
                        <Grid
                          item
                          xs={12}
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          Vagas: <b>{option.vagas}</b> | Selecionados:{" "}
                          <b>{option.selecionados}</b>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vaga"
                      placeholder="Selecione uma vaga..."
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      autoComplete="off"
                      variant="filled"
                      error={
                        formik.touched.codchave &&
                        Boolean(formik.errors.codchave)
                      }
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                {formik.touched.codchave && (
                  <FormHelperText error>
                    {formik.errors.codchave}
                  </FormHelperText>
                )}
              </Grid>
            )}
            {formik.values.type == "3" && (
              <>
                {clientsSys && (
                  <Grid item xs={6} md={6} lg={6}>
                    <Autocomplete
                      id="codchave"
                      disabled={lockCoreFields}
                      //@ts-ignore
                      value={compareClients(formik.values.codchave, clientsSys)}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) =>
                        `${option.id} - (${option.nome})`
                      }
                      options={clientsSys}
                      onChange={(event: any, newValue: ClientsType | null) => {
                        if (newValue) {
                          formik.setFieldValue("codchave", newValue?.id);
                          setAgNomeSting(newValue?.nome);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cliente"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          autoComplete="off"
                          variant="filled"
                          error={
                            formik.touched.codchave &&
                            Boolean(formik.errors.codchave)
                          }
                          fullWidth
                        />
                      )}
                    />
                    {formik.touched.codchave && (
                      <FormHelperText error>
                        {formik.errors.codchave}
                      </FormHelperText>
                    )}
                  </Grid>
                )}

                <Grid item xs={6} md={6} lg={6}>
                  <Autocomplete
                    id="contact_id"
                    disabled={lockCoreFields}
                    open={openAsyncCLients}
                    onOpen={() => {
                      setOpenAsyncClients(true);
                    }}
                    value={compareContactsAg(
                      formik.values.contact_id,
                      optionsContacts,
                    )}
                    onClose={() => {
                      setOpenAsyncClients(false);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.codigo === value.codigo
                    }
                    getOptionLabel={(option) => option.contato}
                    options={optionsContacts}
                    onChange={(
                      event: any,
                      newValue: ClientContactsType | null,
                    ) => {
                      if (newValue) {
                        formik.setFieldValue("contact_id", newValue?.id);
                        setContact(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Contato"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        autoComplete="off"
                        placeholder="Digite para buscar..."
                        variant="filled"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={12}>
              <TextField
                margin="normal"
                size="small"
                fullWidth
                id="description"
                label={t("calendar.form.description.label")}
                name="description"
                rows={4}
                InputLabelProps={{
                  shrink: true,
                }}
                multiline
                variant="filled"
                disabled={lockCoreFields || processing}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />
            </Grid>
          </Grid>
          </Box>

          {showGuestPanel && (
            <Box
              ref={guestPanelRef}
              sx={{
                width: 300,
                flexShrink: 0,
                borderLeft: "1px solid",
                borderColor: "divider",
                pl: 2,
                overflowY: "auto",
                maxHeight: 560,
              }}
            >
              {(formik.values.convidados_ids as number[]).map((userId) => {
                const user = usersSys.find((u) => Number(u.id) === userId);
                if (!user) return null;
                const isLoadingUser = guestEventsMap[userId] === undefined;

                const fcEvents = [
                  ...(guestEventsMap[userId] || [])
                    .filter((e) => !(event?.id && String(e.id) === String(event.id)))
                    .map((e) => ({
                      id: String(e.id),
                      title: e.title || "Ocupado",
                      start: new Date(Number(e.start)),
                      end: new Date(Number(e.end)),
                      backgroundColor: "#ef5350",
                      borderColor: "#c62828",
                      textColor: "#fff",
                    })),
                  {
                    id: "__selected__",
                    title: "Novo",
                    start: formik.values.start,
                    end: formik.values.end,
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.dark || theme.palette.primary.main,
                    textColor: "#fff",
                  },
                ];

                return (
                  <Box key={userId} sx={{ mb: 2.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.78rem" }}
                    >
                      {user.nomecomp}
                    </Typography>

                    {isLoadingUser ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption">Carregando agenda...</Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          "& .fc-timegrid-slot": { height: "30px !important" },
                          "& .fc-timegrid-slot-label-cushion": {
                            fontSize: "0.68rem !important",
                            paddingLeft: "4px !important",
                            paddingRight: "4px !important",
                          },
                          "& .fc-timegrid-axis-cushion": { fontSize: "0.68rem" },
                          "& .fc-col-header": { display: "none" },
                          "& .fc-scrollgrid-section-header": { display: "none" },
                          "& .fc-timegrid-now-indicator-line": { borderColor: "#f44336" },
                          "& .fc-timegrid-now-indicator-arrow": { borderTopColor: "#f44336", borderBottomColor: "#f44336" },
                          "& .fc-event": {
                            borderRadius: "4px !important",
                            cursor: "default !important",
                          },
                          "& .fc-highlight": {
                            background: "rgba(25,118,210,0.15) !important",
                          },
                        }}
                      >
                        <FullCalendar
                          key={`${new Date(formik.values.start).toDateString()}-${userId}`}
                          plugins={[timeGridPlugin, interactionPlugin]}
                          initialView="timeGridDay"
                          initialDate={formik.values.start}
                          headerToolbar={false}
                          allDaySlot={false}
                          locale={ptLocale}
                          slotMinTime="08:00:00"
                          slotMaxTime="18:00:00"
                          slotDuration="00:15:00"
                          slotLabelInterval="00:30:00"
                          slotLabelFormat={{ hour: "numeric", minute: "2-digit", meridiem: false }}
                          height={400}
                          eventMinHeight={28}
                          nowIndicator
                          selectable
                          selectMirror
                          scrollTime={`${String(Math.max(8, new Date(formik.values.start).getHours() - 1)).padStart(2, "0")}:00:00`}
                          events={fcEvents}
                          eventContent={(arg) => (
                            <Box
                              sx={{
                                px: 0.5,
                                py: "1px",
                                overflow: "hidden",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.62rem",
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  color: "#fff",
                                }}
                              >
                                {arg.timeText}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  color: "rgba(255,255,255,0.9)",
                                }}
                              >
                                {arg.event.title}
                              </Typography>
                            </Box>
                          )}
                          dateClick={(info) => {
                            handleStartTimeChange(
                              `${String(info.date.getHours()).padStart(2, "0")}:${String(info.date.getMinutes()).padStart(2, "0")}`,
                            );
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}

            </Box>
          )}

          <Popover
            open={hasGuestConflict && !conflictDismissed}
            anchorEl={guestPanelRef.current}
            onClose={() => setConflictDismissed(true)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            disableRestoreFocus
            PaperProps={{ sx: { borderRadius: 2, boxShadow: 6, maxWidth: 300 } }}
          >
            <Alert
              severity="warning"
              sx={{ alignItems: "flex-start" }}
              action={
                <IconButton
                  size="small"
                  onClick={() => setConflictDismissed(true)}
                  sx={{ mt: 0.25 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <AlertTitle sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                Conflito de horário
              </AlertTitle>
              {conflictingUsers.length === 1 ? (
                <Typography fontSize="0.8rem">
                  <b>{conflictingUsers[0]}</b> não está disponível neste horário.
                </Typography>
              ) : (
                <>
                  <Typography fontSize="0.8rem" sx={{ mb: 0.5 }}>
                    Os seguintes convidados não estão disponíveis:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {conflictingUsers.map((name) => (
                      <Typography
                        key={name}
                        component="li"
                        fontSize="0.78rem"
                        fontWeight={600}
                      >
                        {name}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
              <Typography fontSize="0.72rem" color="text.secondary" sx={{ mt: 1 }}>
                Selecione outro horário disponível na agenda.
              </Typography>
            </Alert>
          </Popover>

          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {event && event.id && !duplicateAsNew && !rescheduleAsNew && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleDuplicate}
                disabled={processing}
              >
                Duplicar
              </Button>
            )}
            {event && event.id && !duplicateAsNew && !rescheduleAsNew && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleReschedule}
                disabled={processing}
              >
                Reagendar
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={onClose}
            >
              {t("common.cancel")}
            </Button>
            <LoadingButton
              size="small"
              loading={processing}
              type="submit"
              variant={
                plainEditMode && !rescheduleAsNew ? "outlined" : "contained"
              }
              color="primary"
              disabled={hasGuestConflict}
            >
              {plainEditMode && !rescheduleAsNew
                ? t("calendar.modal.edit.action")
                : t("calendar.modal.add.action")}
            </LoadingButton>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EventDialog;
